import "../styles/Home.css";
import Navbar from "../components/Navbar.js";
import Footer from "../components/Footer.js";

import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import heroImage from "../assets/hero.jpg";
import whyUsImage from "../assets/why-us-center.jpg";

import aboutImage from "../assets/about.jpg";
import cert1 from "../assets/certificates/cert1.png";
import cert2 from "../assets/certificates/cert2.png";
import isoCert from "../assets/certificates/iso_cert.png";

import product1 from "../assets/products/product1.jpg";
import product2 from "../assets/products/product2.jpg";
import product3 from "../assets/products/product3.jpg";
import product4 from "../assets/products/product4.jpg";
import product5 from "../assets/products/product5.jpg";

import gallery1 from "../assets/gallery/gallery1.jpg";
import gallery2 from "../assets/gallery/gallery2.jpg";
import gallery3 from "../assets/gallery/gallery3.jpg";
import gallery4 from "../assets/gallery/gallery4.jpg";


function CapacityCounter({ target = 100000 }) {
  const { t } = useTranslation();
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const sectionRef = useRef(null);

  // trigger once, when the section enters the viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            setStarted(true);
          }
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);

  // eased count-up, not linear — settles like a real gauge
  useEffect(() => {
    if (!started) return;

    const duration = 2400;
    const startTime = performance.now();
    const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

    let frame;
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = easeOutExpo(progress);
      setCount(Math.floor(eased * target));

      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        setCount(target);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [started, target]);

return (
    <section className="capacity" ref={sectionRef}>
      <div className="capacity-inner">
        <span className="capacity-tag">{t("capacity_tag")}</span>

        <div className="capacity-number">
          <span className="capacity-count">
            {count.toLocaleString("en-US")}
          </span>
          <span className="capacity-plus">+</span>
        </div>

        <p className="capacity-text">
          {t("capacity_text")}
        </p>

        <div className="capacity-bar">
          <div
            className="capacity-bar-fill"
            style={{ width: `${(count / target) * 100}%` }}
          />
        </div>
      </div>
    </section>
  );
}




function Home() {
  const { t } = useTranslation();

  return (
    <div className="home-page">
      <Navbar />

      {/* HERO */}
      <section
        className="hero"
        style={{
          backgroundImage: `url(${heroImage})`,
        }}
      >
        <div className="hero-overlay"></div>

        <div className="hero-content">
          <span className="hero-tag">
            {t("hero_tag")}
          </span>

          <h1>
            {t("hero_title")}
          </h1>

          <p>
            {t("hero_desc")}
          </p>

          <div className="hero-buttons">

            <Link to="/contact">
              <button className="btn primary">
                {t("hero_btn_contact")}
              </button>
            </Link>

            <Link to="/products">
              <button className="btn secondary">
                {t("hero_btn_products")}
              </button>
            </Link>

          </div>
        </div>
      </section>

      {/* CAPACITY */}
      <CapacityCounter target={100000} />
      
      {/* ABOUT */}
      <section className="about">
        <div className="about-image">
          <img src={aboutImage} alt="About EIFJB" />
        </div>

        <div className="about-text">
          <span className="section-tag">
            {t("about_tag")}
          </span>

          <h2>{t("about_title")}</h2>

          <p>
            {t("about_text")}
          </p>

          <Link to="/about">
            <button className="btn primary">
              {t("about_btn")}
            </button>
          </Link>

        </div>
      </section>

      {/* WHY US */}
      <section className="why-us">
        <div className="section-title center">
          <span className="section-tag">
            {t("whyus_tag")}
          </span>

          <h2>{t("whyus_title")}</h2>
        </div>

        <div className="why-us-layout">

          <div className="why-column">

            <div className="info-card">
              <div className="card-icon">✓</div>
              <h3>{t("why_card1_title")}</h3>
              <p>{t("why_card1_text")}</p>
            </div>

            <div className="info-card">
              <div className="card-icon">⏱</div>
              <h3>{t("why_card2_title")}</h3>
              <p>{t("why_card2_text")}</p>
            </div>

            <div className="info-card">
              <div className="card-icon">🌍</div>
              <h3>{t("why_card3_title")}</h3>
              <p>{t("why_card3_text")}</p>
            </div>

          </div>

          <div className="why-image">

            <img
              src={whyUsImage}
              alt="EIFJB Factory"
            />

          </div>

          <div className="why-column">

            <div className="info-card">
              <div className="card-icon">◎</div>
              <h3>{t("why_card4_title")}</h3>
              <p>{t("why_card4_text")}</p>
            </div>

            <div className="info-card">
              <div className="card-icon">⬢</div>
              <h3>{t("why_card5_title")}</h3>
              <p>{t("why_card5_text")}</p>
            </div>

            <div className="info-card">
              <div className="card-icon">🏭</div>
              <h3>{t("why_card6_title")}</h3>
              <p>{t("why_card6_text")}</p>
            </div>

          </div>

        </div>
      </section>

      {/* PRODUCTS */}
<section className="products home-products-section">
  <div className="section-title">
    <span className="section-tag">{t("products_tag")}</span>

    <h2>{t("products_title")}</h2>

    <p>{t("products_desc")}</p>
  </div>

  {/* SCROLLER WRAPPER */}
  <div className="products-wrapper">
    <div className="products-scroll">

      <div className="product-card">
        <div className="product-image-wrapper">
          <img src={product1} alt="Jumbo Bags" />
        </div>
        <div className="product-content">
          <h3>{t("product1_name")}</h3>
        </div>
      </div>

      <div className="product-card">
        <div className="product-image-wrapper">
          <img src={product2} alt="FIBC Bags" />
        </div>
        <div className="product-content">
          <h3>{t("product2_name")}</h3>
        </div>
      </div>

      <div className="product-card">
        <div className="product-image-wrapper">
          <img src={product3} alt="Ventilated Bags" />
        </div>
        <div className="product-content">
          <h3>{t("product3_name")}</h3>
        </div>
      </div>

      <div className="product-card">
        <div className="product-image-wrapper">
          <img src={product4} alt="Agricultural Bags" />
        </div>
        <div className="product-content">
          <h3>{t("product4_name")}</h3>
        </div>
      </div>

      <div className="product-card">
        <div className="product-image-wrapper">
          <img src={product5} alt="Construction Bags" />
        </div>
        <div className="product-content">
          <h3>{t("product5_name")}</h3>
        </div>
      </div>

      

    </div>
  </div>

  <div className="products-footer">
    <Link to="/products">
      <button className="view-all-btn">
        {t("products_view_all")}
      </button>
    </Link>
  </div>
</section>

{/* CERTIFICATES */}
      <section className="certificates">
        <div className="section-title center">
          <span className="section-tag">{t("certificates_tag")}</span>
          <h2>{t("certificates_title")}</h2>
          <p>{t("certificates_desc")}</p>
        </div>
<div className="cert-showcase">
          <figure className="cert-frame">
            <div className="cert-frame-inner">
              <img src={cert1} alt="ISO 9001 Certificate" />
            </div>
            <figcaption>{t("cert1_caption")}</figcaption>
          </figure>

          <figure className="cert-frame cert-frame--iso">
            <div className="cert-frame-inner">
              <img src={isoCert} alt="ISO Certification" />
            </div>
            
          </figure>

          <figure className="cert-frame">
            <div className="cert-frame-inner">
              <img src={cert2} alt="ISO 22000 Certificate" />
            </div>
            <figcaption>{t("cert2_caption")}</figcaption>
          </figure>
        </div>
      </section>

      {/* GALLERY */}
      <section className="gallery">
        <div className="section-title center">
          <span className="section-tag">
            {t("gallery_tag")}
          </span>

          <h2>{t("gallery_title")}</h2>
        </div>

        <div className="gallery-grid">

          <div className="gallery-item">
            <img src={gallery1} alt="factory" />
          </div>

          <div className="gallery-item">
            <img src={gallery2} alt="factory" />
          </div>

          <div className="gallery-item">
            <img src={gallery3} alt="factory" />
          </div>

          <div className="gallery-item">
            <img src={gallery4} alt="factory" />
          </div>

        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <div className="cta-content">
          <h2>
            {t("cta_title")}
          </h2>

          <p>
            {t("cta_desc")}
          </p>

          <Link to="/contact">
            <button className="btn white-btn">
              {t("cta_btn")}
            </button>
          </Link>

        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Home;
